import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Layers, Lock, RotateCcw, Save } from "lucide-react";
import Button from "../../UI/Button";
import { useAppDispatch, useAppSelector } from "../../../hooks/useStore";
import { updateCompanyModules } from "../../../store/slices/superAdminCompanySlice";
import {
  MODULES,
  MODULE_KEYS,
  MODULE_LABEL_KEYS,
  normalizeEnabledModules,
  type ModuleDef,
  type ModuleGroup,
  type ModuleKey,
} from "../../../constants/modules";

interface CompanyModulesCardProps {
  companyId: string;
  /** Effective set from the server. Empty is normalized away server-side. */
  enabledModules?: string[] | null;
  /** Feeds the optimistic-concurrency check on save. */
  updatedAt?: string;
}

/** Section headings, in the order the tenant's own sidebar shows them. */
const GROUP_ORDER: ModuleGroup[] = [
  "main",
  "operations",
  "management",
  "driver",
];

const GROUP_LABELS: Record<ModuleGroup, string> = {
  main: "Main",
  operations: "Operations",
  management: "Management",
  driver: "Driver app",
};

const GROUP_HINTS: Record<ModuleGroup, string> = {
  main: "Always available to admins and users.",
  operations: "The day-to-day console for admins and users.",
  management: "Reference data and tenant configuration.",
  driver:
    "What drivers see in their app. Each one needs its office module switched on.",
};

/**
 * Per-tenant module management.
 *
 * Sends the FULL set on save rather than one toggle at a time — two admins
 * editing the same company would otherwise interleave their changes into a
 * configuration neither of them chose. The server normalizes what arrives and
 * returns the result, and this card renders that answer rather than what it
 * sent, so a dependency the server dropped is visible immediately instead of
 * looking saved until the next reload.
 *
 * Dependencies are enforced here too, live: switching off an office module
 * greys out and unchecks its driver surface as you go, because a driver screen
 * whose API has been switched off is not a state worth letting anyone save.
 */
const CompanyModulesCard: React.FC<CompanyModulesCardProps> = ({
  companyId,
  enabledModules,
  updatedAt,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("navigation");
  const { loading } = useAppSelector((state) => state.superAdminCompanies);

  // What the server last told us, normalized so the comparison below is
  // against the same shape a save would produce.
  const serverSet = useMemo(
    () => normalizeEnabledModules(enabledModules ?? MODULE_KEYS),
    [enabledModules],
  );

  const [selected, setSelected] = useState<ModuleKey[]>(serverSet);
  const [saving, setSaving] = useState(false);

  // Re-seed whenever the company reloads — including right after a save, so the
  // server's normalized answer replaces the local guess.
  useEffect(() => {
    setSelected(serverSet);
  }, [serverSet]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const isDirty = useMemo(
    () =>
      selected.length !== serverSet.length ||
      selected.some((key, i) => key !== serverSet[i]),
    [selected, serverSet],
  );

  /**
   * A module can be toggled unless it is core, or unless its parent is off —
   * in which case it is already forced off and the checkbox would be a lie.
   */
  const isLocked = (mod: ModuleDef) =>
    Boolean(mod.core) || Boolean(mod.dependsOn && !selectedSet.has(mod.dependsOn));

  const toggle = (key: ModuleKey) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      // Normalizing on every click is what makes the dependency rules visible
      // as you work: unchecking Works drops Driver works in the same render,
      // rather than silently at save time.
      return normalizeEnabledModules([...next]);
    });
  };

  const setGroup = (group: ModuleGroup, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const mod of MODULES) {
        if (mod.group !== group || mod.core) continue;
        if (on) next.add(mod.key);
        else next.delete(mod.key);
      }
      return normalizeEnabledModules([...next]);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateCompanyModules({ id: companyId, enabledModules: selected, updatedAt }),
      ).unwrap();
      toast.success("Modules updated successfully");
    } catch (err) {
      // The thunk rejects with the server's message as a plain string.
      toast.error(typeof err === "string" ? err : "Failed to update modules");
      // Put the toggles back to the last known-good server state; a failed
      // save must not leave the UI claiming a configuration that is not live.
      setSelected(serverSet);
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || loading;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Layers className="h-5 w-5 text-gray-400" />
            Modules
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            What this company can reach. Anything switched off disappears from
            their navigation and its API stops answering.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isDirty && (
            <Button
              variant="secondary"
              onClick={() => setSelected(serverSet)}
              disabled={busy}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Reset
            </Button>
          )}
          <Button variant="primary" onClick={handleSave} disabled={!isDirty || busy}>
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? "Saving…" : "Save modules"}
          </Button>
        </div>
      </div>

      <div className="px-6 py-4 space-y-6">
        {GROUP_ORDER.map((group) => {
          const items = MODULES.filter((m) => m.group === group);
          if (items.length === 0) return null;

          const toggleable = items.filter((m) => !m.core);
          const allOn =
            toggleable.length > 0 &&
            toggleable.every((m) => selectedSet.has(m.key));

          return (
            <section key={group}>
              <div className="flex items-baseline justify-between gap-4 mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {GROUP_LABELS[group]}
                  </h3>
                  <p className="text-xs text-gray-500">{GROUP_HINTS[group]}</p>
                </div>

                {toggleable.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setGroup(group, !allOn)}
                    disabled={busy}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {allOn ? "Disable all" : "Enable all"}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {items.map((mod) => {
                  const locked = isLocked(mod);
                  const checked = selectedSet.has(mod.key);

                  return (
                    <label
                      key={mod.key}
                      className={`flex items-start gap-2.5 rounded-lg border p-3 transition-colors ${
                        locked
                          ? "border-gray-100 bg-gray-50 cursor-not-allowed"
                          : "border-gray-200 hover:border-blue-300 cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={locked || busy}
                        onChange={() => toggle(mod.key)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                          {t(MODULE_LABEL_KEYS[mod.key])}
                          {mod.core && (
                            <Lock className="h-3 w-3 text-gray-400 shrink-0" />
                          )}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {mod.core
                            ? "Always on — the company could not be administered without it."
                            : mod.dependsOn
                              ? `Needs ${t(MODULE_LABEL_KEYS[mod.dependsOn])}`
                              : mod.key}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default CompanyModulesCard;
